import { addDays, format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { useEnteringFor, JOHN_DAVIS_PROJECTS } from '@/contexts/EnteringForContext';

interface EnteringForMockGridProps {
  /** The Monday (or configured week-start) of the week currently in view. */
  currentWeekStart: Date;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Lightweight mock timesheet grid rendered in place of the real grid when the
 * user is entering time on behalf of another person.
 *
 * Shows the target person's projects (Project Alpha / Project Beta for John Davis,
 * an empty ready-to-use grid for other people) with empty hour cells to signal
 * that their rules — not Sarah's — drive the data.
 */
export function EnteringForMockGrid({ currentWeekStart }: EnteringForMockGridProps) {
  const { selectedPerson } = useEnteringFor();

  if (!selectedPerson) return null;

  // Only John Davis has specific mock projects; other permitted people show a
  // blank "ready to enter" grid with a generic project placeholder.
  const projects =
    selectedPerson.id === 'john-davis'
      ? JOHN_DAVIS_PROJECTS
      : [{ id: 'placeholder', name: '— No projects assigned —', client: '' }];

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const isPlaceholder = selectedPerson.id !== 'john-davis';

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2 sm:gap-3 pb-1 overflow-auto">
      <Card className="border shadow-sm">
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm border-collapse min-w-[640px]">
            {/* Column headers */}
            <thead>
              <tr className="bg-muted/40 border-b">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-[220px]">
                  Project / Activity
                </th>
                {days.map((day, i) => (
                  <th
                    key={i}
                    className="px-2 py-2.5 text-center font-medium text-muted-foreground w-16"
                  >
                    <span className="block text-xs">{DAY_LABELS[i]}</span>
                    <span className="block text-[11px] text-muted-foreground/70">
                      {format(day, 'd MMM')}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2.5 text-center font-medium text-muted-foreground w-16">
                  Total
                </th>
              </tr>
            </thead>

            {/* Project rows */}
            <tbody>
              {projects.map((project, rowIdx) => (
                <tr
                  key={project.id}
                  className={rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                >
                  {/* Project name cell */}
                  <td className="px-4 py-2.5 border-b">
                    <div>
                      <span
                        className={
                          isPlaceholder
                            ? 'text-muted-foreground italic text-sm'
                            : 'font-medium text-sm'
                        }
                      >
                        {project.name}
                      </span>
                      {project.client && (
                        <span className="block text-xs text-muted-foreground">
                          {project.client}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Hour cells — empty, ready to enter */}
                  {days.map((_, i) => (
                    <td key={i} className="px-2 py-2 text-center border-b border-l border-border/40">
                      <div
                        className="mx-auto h-8 w-12 rounded border border-input bg-background
                                   text-sm text-center flex items-center justify-center
                                   text-muted-foreground/30 cursor-text select-none"
                        title={isPlaceholder ? 'No projects assigned' : 'Enter hours'}
                        aria-label={`${project.name} hours for ${DAY_LABELS[i]}`}
                      >
                        —
                      </div>
                    </td>
                  ))}

                  {/* Row total */}
                  <td className="px-3 py-2 text-center border-b border-l font-medium text-muted-foreground">
                    0:00
                  </td>
                </tr>
              ))}

              {/* Week total row */}
              <tr className="bg-muted/30 font-semibold text-sm">
                <td className="px-4 py-2.5 text-right text-muted-foreground">Week Total</td>
                {days.map((_, i) => (
                  <td key={i} className="px-2 py-2.5 text-center border-l text-muted-foreground">
                    0:00
                  </td>
                ))}
                <td className="px-3 py-2.5 text-center border-l text-muted-foreground">0:00</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Explanatory note */}
      {!isPlaceholder && (
        <p className="text-xs text-muted-foreground px-1">
          Showing <strong>{selectedPerson.name}</strong>'s projects and cost centres. Enter hours
          above and submit — the approval workflow assigned to{' '}
          <strong>{selectedPerson.name}</strong> will apply.
        </p>
      )}
    </div>
  );
}
